import { captureException } from '@sentry/react';
import { toast } from 'sonner';
import { DataContextValue, TRANSACTION_API_PATH, VENDOR_API_PATH } from '../../../contexts/data-context/data-provider';
import { VendorView } from '../../../types/entity-types';

type EditVendorParams = {
  vendor: VendorView;
  name: string;
  categoryId: string;
  description?: string;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  data: DataContextValue['data'];
  mutations: DataContextValue['mutations'];
  vendors: VendorView[];
};

type EditVendorResult = {
  success: boolean;
  updatedVendor?: VendorView;
  isDuplicate?: boolean;
  duplicateVendorIds?: string[];
  incrementalTransactionIds?: string[];
};

export const editVendor = async ({
  vendor,
  name,
  categoryId,
  description,
  authenticatedFetch,
  data,
  mutations,
  vendors,
}: EditVendorParams): Promise<EditVendorResult> => {
  try {
    // Check for duplicate vendors
    const duplicateVendors = vendors.filter((v) => v.name === name && v.id !== vendor.id);

    if (duplicateVendors.length > 0) {
      return {
        success: false,
        isDuplicate: true,
        duplicateVendorIds: duplicateVendors.map((v) => v.id),
        incrementalTransactionIds: duplicateVendors.flatMap(
          (v) => v.transactions?.filter(Boolean).map((t) => t.id) || [],
        ),
      };
    }

    const patchOperation = [
      {
        id: vendor.id,
        patch: [
          {
            op: 'replace',
            path: '/name',
            value: name,
          },
          {
            op: 'replace',
            path: '/categoryId',
            value: categoryId,
          },
          {
            op: 'replace',
            path: '/description',
            value: description,
          },
        ],
      },
    ];

    const response = await authenticatedFetch(VENDOR_API_PATH, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patchOperation),
    });

    if (!response.ok) {
      throw new Error('Failed to update vendor');
    }

    // Find the updated category
    const updatedCategory = data?.categories.find((c) => c.id === categoryId);

    // Create the updated vendor object
    const updatedVendor: VendorView = {
      ...vendor,
      name,
      categoryId,
      category: updatedCategory,
      description,
    };

    // Update the cache
    await mutations.mutateCache?.({
      vendors: data?.vendors.map((v) => (v.id === vendor.id ? updatedVendor : v)) ?? [],
      transactions:
        data?.transactions.map((t) =>
          t.vendor?.id === vendor.id
            ? {
                ...t,
                vendor: updatedVendor,
                description: name,
                category: updatedCategory,
              }
            : t,
        ) ?? [],
    });

    return {
      success: true,
      updatedVendor,
    };
  } catch (error) {
    toast.error('Failed to update vendor');
    captureException(error);
    return {
      success: false,
    };
  }
};

export const editVendorAndMergeTransactions = async ({
  vendor,
  name,
  categoryId,
  description,
  authenticatedFetch,
  data,
  mutations,
  vendors,
  duplicateVendorIds,
  incrementalTransactionIds,
}: EditVendorParams & {
  duplicateVendorIds: string[];
  incrementalTransactionIds: string[];
}): Promise<EditVendorResult> => {
  try {
    // Use the logo of the first duplicate vendor to merge the transactions
    const duplicateVendorLogo = vendors.find((v) => v.id === duplicateVendorIds[0])?.logo;

    const patchVendorOperation = [
      {
        id: vendor.id,
        patch: [
          {
            op: 'replace',
            path: '/name',
            value: name,
          },
          {
            op: 'replace',
            path: '/categoryId',
            value: categoryId,
          },
          {
            op: 'replace',
            path: '/description',
            value: description,
          },
          {
            op: 'replace',
            path: '/logo',
            value: duplicateVendorLogo,
          },
        ],
      },
    ];

    const patchTransactionOperations = incrementalTransactionIds.map((txnId) => [
      {
        id: txnId,
        patch: [
          {
            op: 'replace',
            path: '/vendorId',
            value: vendor.id,
          },
        ],
      },
    ]);

    // Update vendor
    const vendorResponse = await authenticatedFetch(VENDOR_API_PATH, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patchVendorOperation),
    });

    if (!vendorResponse.ok) {
      throw new Error('Failed to update vendor');
    }

    // Update transactions
    await Promise.all(
      patchTransactionOperations.map(async (txn) => {
        const response = await authenticatedFetch(TRANSACTION_API_PATH, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(txn),
        });

        if (!response.ok) {
          throw new Error('Failed to update transaction');
        }
      }),
    );

    // Delete duplicate vendors
    const deleteResponse = await authenticatedFetch(VENDOR_API_PATH, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(duplicateVendorIds),
    });

    if (!deleteResponse.ok) {
      throw new Error('Failed to delete duplicate vendors');
    }

    toast.success('Transactions merged successfully');

    // Find the updated category
    const updatedCategory = data?.categories.find((c) => c.id === categoryId);

    // Create the updated vendor object
    const updatedVendor: VendorView = {
      ...vendor,
      name,
      categoryId,
      category: updatedCategory,
      description,
      logo: duplicateVendorLogo,
    };

    // Update the cache
    await mutations.mutateCache?.({
      vendors:
        data?.vendors
          .filter((v) => !duplicateVendorIds.includes(v.id))
          .map((v) => (v.id === vendor.id ? updatedVendor : v)) ?? [],
      transactions:
        data?.transactions.map((t) =>
          t.vendor?.id === vendor.id || incrementalTransactionIds.includes(t.id)
            ? {
                ...t,
                vendorId: vendor.id,
                vendor: updatedVendor,
                description: name,
                category: updatedCategory,
              }
            : t,
        ) ?? [],
    });

    return {
      success: true,
      updatedVendor,
    };
  } catch (error) {
    toast.error('Failed to merge transactions');
    captureException(error);
    return {
      success: false,
    };
  }
};
